import { Timestamp, FieldValue } from "firebase/firestore";

/**
 * Helper function to check if a value is a serverTimestamp placeholder
 */
const isServerTimestamp = (value: any): boolean => {
  return value && typeof value === 'object' && 
    (value instanceof FieldValue || 
     (value._methodName === 'serverTimestamp') ||
     (value._delegate && value._delegate._methodName === 'serverTimestamp'));
};

/**
 * Converts any timestamp-like object to a proper Firestore Timestamp
 * Handles:
 * - ServerTimestamp placeholders (returned as-is)
 * - Existing Timestamp objects
 * - Objects with seconds/nanoseconds properties (both formats)
 * - Date objects
 * - ISO date strings
 */
export function ensureFirestoreTimestamp(value: any): Timestamp | FieldValue | null {
  if (!value) return null;
  
  // CRITICAL: If it's a serverTimestamp placeholder, return it as-is
  // This preserves the placeholder so Firestore can resolve it during write
  if (isServerTimestamp(value)) {
    console.log('[ensureFirestoreTimestamp] Preserving serverTimestamp() placeholder');
    return value;
  }
  
  // Already a Timestamp
  if (value instanceof Timestamp) return value;
  
  // 🔥 CRITICAL FIX: Handle object with seconds and nanoseconds (serialized Timestamp from Firestore)
  // Firestore returns timestamps as {_seconds, _nanoseconds} when read with getDoc()
  if (typeof value === 'object' && value !== null) {
    // Prioritize Firestore's serialized format (_seconds, _nanoseconds) over direct format
    const seconds = value._seconds !== undefined ? value._seconds : value.seconds;
    const nanoseconds = value._nanoseconds !== undefined ? value._nanoseconds : value.nanoseconds;
    
    if (typeof seconds === 'number' && typeof nanoseconds === 'number') {
      console.log('[ensureFirestoreTimestamp] Converting serialized Timestamp to Firestore Timestamp');
      return new Timestamp(seconds, nanoseconds);
    }
  }
  
  // Handle Date objects
  if (value instanceof Date) {
    return Timestamp.fromDate(value);
  }
  
  // Handle ISO strings
  if (typeof value === 'string') {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return Timestamp.fromDate(date);
    }
  }
  
  // Handle numeric timestamps (milliseconds since epoch)
  if (typeof value === 'number') {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return Timestamp.fromDate(date);
    }
  }
  
  console.warn("Could not convert to Timestamp:", value);
  return null;
}

/**
 * 🔥 CRITICAL FUNCTION: Forces all timestamp fields to be proper Firestore Timestamps
 * This runs as the ABSOLUTE FINAL step before any Firestore write to prevent corruption.
 * 
 * @param data - The data object to be written to Firestore
 * @returns The same data with all timestamp fields forced to proper Firestore Timestamps
 */
export function forceFirestoreTimestamps(data: Record<string, any>): Record<string, any> {
  console.log('[forceFirestoreTimestamps] Forcing timestamp format compliance before Firestore write');
  
  const result = { ...data };
  
  // List of known timestamp fields that MUST be Firestore Timestamps
  const TIMESTAMP_FIELDS = [
    'createdAt',
    'lastEditedAt', 
    'lastPublishedAt',
    'publishedAt'
  ];

  const convertKnownFields = (obj: any) => {
    for (const field of TIMESTAMP_FIELDS) {
      if (obj[field] !== undefined && obj[field] !== null) {
        const forcedTimestamp = ensureFirestoreTimestamp(obj[field]);
        if (forcedTimestamp) {
          obj[field] = forcedTimestamp;
          console.log(`[forceFirestoreTimestamps] Forced ${field} to proper Firestore Timestamp`);
        } else {
          // Leave as-is if cannot convert (e.g., null/undefined already handled)
        }
      }
    }
  };

  // Stricter plain object test (excludes class instances)
  const isPlainObject = (val: any) =>
    Object.prototype.toString.call(val) === '[object Object]';

  // Robust detection for Firestore DocumentReference (avoid recursion into it)
  const isDocumentReference = (val: any) =>
    val &&
    typeof val === 'object' &&
    // DocumentReference always has withConverter and path/id on the instance/prototype
    'withConverter' in val &&
    ('path' in val || 'id' in val);

  const shouldRecurseInto = (val: any) => {
    return isPlainObject(val)
      && !(val instanceof Timestamp)
      && !(val instanceof FieldValue)
      && !isServerTimestamp(val)
      && !isDocumentReference(val);
  };

  // Cycle guard to avoid infinite recursion on unexpected graphs
  const seen = new WeakSet<object>();

  const recurse = (obj: any) => {
    if (obj === null || typeof obj !== 'object') return;
    if (!shouldRecurseInto(obj)) return;

    if (seen.has(obj)) return;
    seen.add(obj);

    // First, convert known fields on this object
    convertKnownFields(obj);

    // Recurse into nested objects
    for (const [key, value] of Object.entries(obj)) {
      if (shouldRecurseInto(value)) {
        recurse(value);
      }
    }
  };
  
  // Force main document timestamp fields
  convertKnownFields(result);
  
  // 🔥 CRITICAL: Force photo array timestamp fields
  // Firebase does NOT allow serverTimestamp() inside arrays!
  if (result.photos && Array.isArray(result.photos)) {
    result.photos = result.photos.map((photo, index) => {
      if (photo && typeof photo === 'object') {
        const processedPhoto = { ...photo };
        
        // Force photo-specific timestamp fields
        const PHOTO_TIMESTAMP_FIELDS = ['createdAt', 'updatedAt'];
        
        for (const field of PHOTO_TIMESTAMP_FIELDS) {
          if (processedPhoto[field] !== undefined && processedPhoto[field] !== null) {
            
            // 🔥 CRITICAL: Firebase doesn't allow serverTimestamp() inside arrays!
            // Convert serverTimestamp() to Timestamp.now() for array elements
            if (isServerTimestamp(processedPhoto[field])) {
              processedPhoto[field] = Timestamp.now();
              console.log(`[forceFirestoreTimestamps] Converted serverTimestamp() to Timestamp.now() for photos[${index}].${field} (Firebase array limitation)`);
            } else {
              // Handle other timestamp formats normally
              const forcedTimestamp = ensureFirestoreTimestamp(processedPhoto[field]);
              if (forcedTimestamp) {
                processedPhoto[field] = forcedTimestamp;
                console.log(`[forceFirestoreTimestamps] Forced photos[${index}].${field} to proper Firestore Timestamp`);
              } else {
                console.error(`[forceFirestoreTimestamps] Failed to convert photos[${index}].${field} to Timestamp:`, processedPhoto[field]);
                // Remove the corrupted field rather than write a map
                delete processedPhoto[field];
              }
            }
          }
        }
        
        return processedPhoto;
      }
      return photo;
    });
  }
  
  // Recurse into nested objects beyond one level
  recurse(result);
  
  console.log('[forceFirestoreTimestamps] Timestamp format compliance check completed');
  return result;
}
